import { type Dispatch } from "redux";
import { type FiltersMap } from "utils/filterUtils";
import accountsDataService from "features/Accounts/services/accountsDataService";
import DataService from "features/Application/services/dataServices/typedDataService";
import { type AxiosResponse } from "axios";
import { type UsersFilterOptions } from "features/People/types";
import * as filterActionTypes from "./enrollmentFilterActionTypes";

export interface EnrollmentFilterOptionParams {
  shouldGetOfficeLocations: boolean;
  shouldGetManagers: boolean;
  shouldGetLicenses: boolean;
  shouldGetDefaultRoles: boolean;
  accountTypeId: number;
  shouldGetGroups: boolean;
}

export const getEnrollmentFilterOptions = (filterOptionParams?: EnrollmentFilterOptionParams) => {
  filterOptionParams = filterOptionParams || {
    shouldGetOfficeLocations: true,
    shouldGetManagers: true,
    shouldGetLicenses: true,
    shouldGetDefaultRoles: true,
    accountTypeId: 1,
    shouldGetGroups: false,
  };

  const getFilterOptionsBegin = () => ({
    type: filterActionTypes.PEOPLE_GROUPS_ENROLLMENT_FILTER_OPTIONS.FETCH_OPTIONS_BEGIN,
  });

  const getFilterOptionsSuccess = (filterOptions: FiltersMap) => ({
    type: filterActionTypes.PEOPLE_GROUPS_ENROLLMENT_FILTER_OPTIONS.FETCH_OPTIONS_SUCCESS,
    payload: { ...filterOptions },
  });

  const getFilterOptionsFailed = (error: Error) => ({
    type: filterActionTypes.PEOPLE_GROUPS_ENROLLMENT_FILTER_OPTIONS.FETCH_OPTIONS_FAILURE,
    payload: { error },
  });

  const mapData = (data: AxiosResponse<string[]>) =>
    data.data.filter((x: string) => x !== "" && x !== null).map((x: string) => ({ text: x, value: x }));

  const mapLicenses = (data: { title: string; skuId: string }[]) =>
    data.map((x) => ({ text: x.title, value: x.skuId }));

  const mapDefaultRoles = (data: { name: string }[]) => data.map((x) => ({ text: x.name, value: x.name }));

  const mapGroups = (data: { name: string; id: string }[]) => data.map((x) => ({ text: x.name, value: x.id }));

  return async (dispatch: Dispatch) => {
    dispatch(getFilterOptionsBegin());

    try {
      let officeLocations = null;
      let managers = null;
      let licenses = null;
      let defaultRoles = null;
      let groups = null;

      if (filterOptionParams.shouldGetOfficeLocations) {
        const result: AxiosResponse<string[]> = await DataService.users.getOfficeLocations();
        officeLocations = mapData(result);
      }
      if (filterOptionParams.shouldGetManagers) {
        const result: AxiosResponse<string[]> = await DataService.users.getManagers();
        managers = mapData(result);
      }
      if (filterOptionParams.shouldGetLicenses) {
        const result = await DataService.users.getLicenses();
        licenses = mapLicenses(result);
      }
      if (filterOptionParams.shouldGetDefaultRoles) {
        const result = await accountsDataService.getAccountTypeRoles(filterOptionParams.accountTypeId);
        defaultRoles = mapDefaultRoles(result);
      }
      if (filterOptionParams.shouldGetGroups) {
        const result = await DataService.users.getAccountGroups();
        groups = mapGroups(result);
      }

      const departments: AxiosResponse<string[]> = await DataService.users.getDepartments();
      const jobTitles: AxiosResponse<string[]> = await DataService.users.getJobTitles();

      const filterOptions: UsersFilterOptions = {
        departments: mapData(departments),
        jobTitles: mapData(jobTitles),
        officeLocations,
        managers,
        roles: null,
        licenses,
        defaultRoles,
        groups,
      };

      dispatch(getFilterOptionsSuccess(filterOptions));
    } catch (error) {
      dispatch(getFilterOptionsFailed(error as Error));
    }
  };
};
